import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Stack,
  CircularProgress,
} from "@mui/material";

import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";
import FingerprintOutlinedIcon from "@mui/icons-material/FingerprintOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";

import { getUser } from "../../api/users";
import { useUserProvider } from "../../providers/useUserProvider";

type User = {
  id_utente: number;
  nome: string;
  cognome: string;
  codice_fiscale: string;
  data_nascita: string;
  email: string;
};

const cardSx = {
  bgcolor: "background.paper",
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  boxShadow: "none",
};

const sectionTitleSx = {
  color: "#fff",
  fontWeight: 800,
};

const sectionSubtitleSx = {
  color: "rgba(255,255,255,0.9)",
};

// riga info riutilizzabile
const InfoRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value?: string | number | null;
}> = ({ icon, label, value }) => {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            border: "1px solid",
            borderColor: "divider",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: "text.secondary",
          }}
        >
          {icon}
        </Box>
        <Typography variant="subtitle2" color="text.secondary">
          {label}
        </Typography>
      </Stack>

      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {value && String(value).trim() !== "" ? value : "—"}
      </Typography>
    </Box>
  );
};

function formatDateISOToIT(dateISO?: string | null): string {
  if (!dateISO) return "—";
  const d = new Date(dateISO);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("it-IT");
}

const ViewProfile: React.FC = () => {
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useUserProvider();

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        if (user?.userId == null) return;
        const data = await getUser(user.userId);
        setUserProfile(data as User);
      } catch (err) {
        console.error("Errore caricamento profilo:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchUserProfile();
  }, [user?.userId]);

  const fullName = useMemo(() => {
    const n = userProfile?.nome?.trim() ?? "";
    const c = userProfile?.cognome?.trim() ?? "";
    return [n, c].filter(Boolean).join(" ") || "—";
  }, [userProfile?.nome, userProfile?.cognome]);

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="70vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "calc(100vh - 64px)" }}>
      {/* Titolo & sottotitolo in stile “Statistiche” */}
      <Box sx={{ px: { xs: 3, md: 6 }, pt: 2, pb: 1 }}>
        <Typography variant="h5" sx={sectionTitleSx}>
          Profilo Utente
        </Typography>
        <Typography variant="subtitle2" sx={sectionSubtitleSx}>
          Visualizza e gestisci le informazioni del tuo profilo.
        </Typography>
      </Box>

      <Container maxWidth="xl" sx={{ pb: 4 }}>
        <Card sx={{ ...cardSx, p: 2 }}>
          <CardContent sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              {/* Nome e Cognome su 2 colonne */}
              <Grid item xs={12} md={6}>
                <InfoRow
                  icon={<BadgeOutlinedIcon fontSize="small" />}
                  label="Nome"
                  value={userProfile?.nome}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InfoRow
                  icon={<BadgeOutlinedIcon fontSize="small" />}
                  label="Cognome"
                  value={userProfile?.cognome}
                />
              </Grid>

              {/* Full name in evidenza, opzionale */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "action.hover",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <AccountCircleOutlinedIcon sx={{ color: "text.secondary" }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Nome completo
                  </Typography>
                  <Typography sx={{ fontWeight: 700, ml: "auto" }}>
                    {fullName}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <InfoRow
                  icon={<CakeOutlinedIcon fontSize="small" />}
                  label="Data di nascita"
                  value={formatDateISOToIT(userProfile?.data_nascita)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <InfoRow
                  icon={<FingerprintOutlinedIcon fontSize="small" />}
                  label="Codice Fiscale"
                  value={userProfile?.codice_fiscale}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <InfoRow
                  icon={<EmailOutlinedIcon fontSize="small" />}
                  label="Email"
                  value={userProfile?.email}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <InfoRow
                  icon={<BadgeOutlinedIcon fontSize="small" />}
                  label="ID Utente"
                  value={userProfile?.id_utente}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default ViewProfile;
